import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";

import {
  BulkImportRequestFragmentFragment,
  useUploadBulkUserImportMutation,
} from "~/components/Admin/User/BulkImport/BulkImport.generated";
import { PrimaryButton, SecondaryButton } from "~/components/common/buttons";

interface Role {
  id: string;
  name: string;
}

interface Props {
  orgSlug: string;
  roles: Role[];
  onSuccess: (
    requestId: string,
    request: BulkImportRequestFragmentFragment
  ) => void;
  onValidationErrors: (
    errors: Array<{
      sheet: string;
      row?: number | null;
      column?: string | null;
      message: string;
    }>
  ) => void;
  onCancel: () => void;
}

export const UploadStep = ({
  orgSlug,
  roles,
  onSuccess,
  onValidationErrors,
  onCancel,
}: Props) => {
  const [file, setFile] = useState<File | null>(null);
  const [roleId, setRoleId] = useState<string>("");
  const [uploading, setUploading] = useState(false);
  const [uploadBulkUserImport] = useUploadBulkUserImportMutation();

  const onDrop = useCallback((accepted: File[]) => {
    if (accepted.length > 0) setFile(accepted[0]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ".xlsx,.csv",
    multiple: false,
  });

  const handleUpload = async () => {
    if (!file || !roleId) return;
    setUploading(true);
    try {
      const result = await uploadBulkUserImport({
        variables: { orgSlug, file, roleId },
      });
      const payload = result.data?.uploadBulkUserImport;
      if (!payload) return;
      if (payload.errors && payload.errors.length > 0) {
        onValidationErrors([...payload.errors]);
      } else if (payload.request) {
        onSuccess(payload.request.id, payload.request);
      }
    } finally {
      setUploading(false);
    }
  };

  const xlsxTemplateUrl = `/cleancapital/admin/${orgSlug}/bulk-imports/template.xlsx`;
  const csvTemplateUrl = `/cleancapital/admin/${orgSlug}/bulk-imports/template.xlsx?fmt=csv`;
  const canSubmit = !!file && !!roleId && !uploading;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
      <p style={{ margin: 0, color: "#666", fontSize: "0.875rem" }}>
        Upload a spreadsheet (max 50 rows) to add users and user groups in one
        step. Accepts <strong>.xlsx</strong> (two-sheet) or{" "}
        <strong>.csv</strong> (Users columns only).
      </p>

      {/* Role selector */}
      <div>
        <label
          htmlFor="bulk-import-role"
          style={{
            display: "block",
            fontWeight: 500,
            marginBottom: "0.35rem",
            fontSize: "0.875rem",
          }}
        >
          Role for new groups <span style={{ color: "#dc3545" }}>*</span>
        </label>
        <select
          id="bulk-import-role"
          value={roleId}
          onChange={(e) => setRoleId(e.target.value)}
          required
          style={{
            width: "100%",
            maxWidth: 320,
            padding: "0.45rem 0.75rem",
            border: roleId ? "1px solid #ced4da" : "1px solid #dc3545",
            borderRadius: 4,
            fontSize: "0.9rem",
            background: "#fff",
            color: roleId ? "inherit" : "#999",
            appearance: "auto",
          }}
        >
          <option value="" disabled>
            Select a role…
          </option>
          {roles.map((r) => (
            <option key={r.id} value={r.id} style={{ color: "inherit" }}>
              {r.name}
            </option>
          ))}
        </select>
        {!roleId && (
          <p
            style={{
              margin: "0.25rem 0 0",
              fontSize: "0.78rem",
              color: "#dc3545",
            }}
          >
            A role is required to set permissions for any new groups created by
            this import.
          </p>
        )}
      </div>

      {/* Drop zone */}
      <div
        {...getRootProps()}
        style={{
          border: `2px dashed ${isDragActive ? "#0d6efd" : "#ccc"}`,
          borderRadius: 6,
          padding: "2rem 1.5rem",
          textAlign: "center",
          cursor: "pointer",
          background: isDragActive ? "#f0f6ff" : "#fafafa",
          transition: "border-color 0.15s, background 0.15s",
        }}
      >
        <input {...getInputProps()} />
        {file ? (
          <p style={{ margin: 0, fontWeight: 500 }}>
            📄 {file.name}
            <span
              style={{
                marginLeft: "0.5rem",
                color: "#888",
                fontSize: "0.8rem",
                fontWeight: 400,
              }}
            >
              (click to change)
            </span>
          </p>
        ) : isDragActive ? (
          <p style={{ margin: 0, color: "#0d6efd" }}>Drop it here…</p>
        ) : (
          <p style={{ margin: 0, color: "#555" }}>
            Drag & drop a <strong>.xlsx</strong> or <strong>.csv</strong> file
            here, or{" "}
            <span style={{ color: "#0d6efd", textDecoration: "underline" }}>
              browse
            </span>
          </p>
        )}
      </div>

      {/* Footer: template links left, action buttons right */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div style={{ fontSize: "0.8rem", color: "#666" }}>
          Download template:{" "}
          <a href={xlsxTemplateUrl} download style={{ marginRight: "0.5rem" }}>
            XLSX
          </a>
          ·{" "}
          <a href={csvTemplateUrl} download style={{ marginLeft: "0.5rem" }}>
            CSV
          </a>
        </div>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <SecondaryButton onClick={onCancel} disabled={uploading}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={handleUpload} disabled={!canSubmit}>
            {uploading ? "Uploading…" : "Upload & Validate"}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};
